import { Toggle } from '@fluentui/react';
import { Dropdown, toggleStylesDefault, useTheme } from '@h2oai/ui-kit';
import { Chart, ChartDataset } from 'chart.js';
import { useEffect, useMemo, useRef, useState } from 'react';

import { lineChartOptions } from '../../../common/utils';
import LineChart from '../../LineChart/LineChart';
import { chartWrapperStyle, monthSelectDropdownStyles, PageStyle, statBarStyle } from '../AIUnitHourly.styles';
import { useAIUnitHourlyCumulativeData } from '../hooks';
import { getAIUnitHourlyMonthDropdownOptions, getMonthRange } from '../utils';

interface ICumulativeSummaryProps {
  envStartDate: string;
  envEndDate: string;
}

export default function CumulativeSummary({ envStartDate, envEndDate }: ICumulativeSummaryProps) {
  const [showComponentCumulativeView, setShowComponentCumulativeView] = useState<boolean>(false);
  const [selectedPeriodKey, setSelectedPeriodKey] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [visibleTotal, setVisibleTotal] = useState<number>(0);

  const chartRef = useRef<Chart | null>(null);

  const theme = useTheme();

  useEffect(() => {
    if (!envStartDate || !envEndDate || envStartDate === '0000-00-00' || envEndDate === '0000-00-00') {
      setSelectedPeriodKey('entire-period');
      setStartDate(null);
      setEndDate(null);
    } else {
      setSelectedPeriodKey('contract-period');
      const environmentStartDate = new Date(envStartDate);
      const environmentEndDate = new Date(envEndDate);

      const firstDate = new Date(
        Date.UTC(environmentStartDate.getFullYear(), environmentStartDate.getMonth(), environmentStartDate.getDate())
      );
      const lastDate = new Date(
        Date.UTC(environmentEndDate.getFullYear(), environmentEndDate.getMonth(), environmentEndDate.getDate())
      );
      setStartDate(firstDate);
      setEndDate(lastDate);
    }

    setIsInitialized(true);
  }, [envStartDate, envEndDate]);

  const {
    augmentedData,
    threshold,
    totalUsage,
    firstDate,
    lastDate,
    loading: dataLoading,
    error,
    isEmpty,
  } = useAIUnitHourlyCumulativeData(
    isInitialized ? startDate : null,
    isInitialized ? endDate : null,
    showComponentCumulativeView
  );

  const loading = dataLoading || !isInitialized;

  useEffect(() => {
    if (showComponentCumulativeView && augmentedData?.datasets) {
      const datasets = augmentedData.datasets;
      const lastIndex = (augmentedData.labels?.length || 1) - 1;

      const initialVisibleSum = datasets
        .filter((dataset: any) => dataset.label !== 'Threshold')
        .reduce((sum: number, dataset: any) => {
          const lastValue = dataset.data[lastIndex];
          return sum + (typeof lastValue === 'number' ? lastValue : 0);
        }, 0);

      setVisibleTotal(initialVisibleSum);
    }
  }, [showComponentCumulativeView, augmentedData, dataLoading]);

  const updateVisibleTotal = (chart: Chart<'line'>) => {
    if (!chart.data.datasets || !chart.data.labels) return;

    const datasets = chart.data.datasets;
    const lastIndex = chart.data.labels.length - 1;

    const visibleSum = datasets
      .filter((dataset, idx) => {
        if (dataset.label === 'Threshold') return false;
        const meta = chart.getDatasetMeta(idx);
        return meta ? !meta.hidden : true;
      })
      .reduce((sum, dataset: ChartDataset<'line'>) => {
        const lastValue = dataset.data[lastIndex];
        return sum + (typeof lastValue === 'number' ? lastValue : 0);
      }, 0);

    setVisibleTotal(visibleSum);
  };

  const totalPlugin = {
    id: 'cumulativeTotalPlugin',
    afterEvent(chart: Chart<'line'>, args: any) {
      const e = args.event;
      if (e.type === 'click') {
        updateVisibleTotal(chart);
      }
    },
    afterDatasetsDraw(chart: Chart<'line'>) {
      updateVisibleTotal(chart);
    },
  };

  const getUsagePercentage = (): string => {
    if (threshold) {
      const total = showComponentCumulativeView ? visibleTotal : totalUsage;
      return `${((total / threshold) * 100).toFixed(2)}%`;
    } else {
      return 'N/A';
    }
  };

  const handleShowComponentCumulativeViewToggle = (_: React.MouseEvent<HTMLElement>, checked?: boolean) => {
    setShowComponentCumulativeView(!!checked);
  };

  const chartOptions = useMemo(() => {
    return lineChartOptions(
      `AI Unit Usage - ${getUsagePercentage()}`,
      augmentedData && augmentedData.labels?.length ? augmentedData.labels?.length : 0,
      16
    );
  }, [visibleTotal, threshold, totalUsage, showComponentCumulativeView, augmentedData]);

  return (
    <>
      <div style={PageStyle}>
        <div style={statBarStyle}>
          <Toggle
            inlineLabel
            label="Show by Component"
            checked={showComponentCumulativeView}
            onChange={handleShowComponentCumulativeViewToggle}
            styles={toggleStylesDefault(theme)}
            disabled={isEmpty || loading}
          />
          <Dropdown
            options={getAIUnitHourlyMonthDropdownOptions(firstDate || '', lastDate || '')}
            selectedKey={selectedPeriodKey}
            styles={monthSelectDropdownStyles}
            disabled={isEmpty || loading}
            onChange={(_, option) => {
              if (!option) return;
              setSelectedPeriodKey(option.key as string);

              if (option.key === 'entire-period') {
                setStartDate(null);
                setEndDate(null);
              } else if (option.key === 'contract-period') {
                setStartDate(new Date(envStartDate || ''));
                setEndDate(new Date(envEndDate || ''));
              } else {
                const { firstDate, lastDate } = getMonthRange(option.key as string);
                setStartDate(new Date(firstDate));
                setEndDate(new Date(lastDate));
              }
            }}
          />
        </div>
        <div style={chartWrapperStyle}>
          <LineChart
            ref={(ref) => {
              if (ref) {
                chartRef.current = ref;
              }
            }}
            options={chartOptions}
            data={augmentedData}
            loading={loading}
            plugins={[totalPlugin]}
            error={error ? true : false}
          />
        </div>
      </div>
    </>
  );
}
